'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Button } from '../../../components/button';
import { Skeleton } from '../../../components/skeleton';
import { BodyDefault } from '../../../components/typography';
import { toasterApi } from '../../toast/ToastProvider';
import { SettingsBooleanControl } from '../../settings/SettingsBooleanControl';
import { InternalAccessBindingEditor } from './InternalAccessBindingEditor';
import { InternalAccessTestResults } from './InternalAccessTestResults';
import {
  ManageNetworkLinkButton,
  ManageNetworkSetupCallout,
} from './ManageNetworkSetupCallout';
import {
  buildInternalNetworkOptions,
  hasInternalConnectors,
  type ApplicationInternalAccess,
  type InternalAccessBindingPayload,
  type InternalAccessNetworkSummary,
  type InternalAccessTestResult,
  type InternalConnectorSummary,
  type InternalNetworkSummary,
  type InternalNetworkTransportFlags,
} from './internal-access.model';
import type { InternalApplicationDraft } from './useInternalApplicationDraft';

export type InternalNetworkInventory = {
  twingateNetworks: readonly InternalNetworkSummary[];
  internalAccessNetworks: readonly InternalAccessNetworkSummary[];
  connectors: readonly InternalConnectorSummary[];
  flags: InternalNetworkTransportFlags;
  areFeatureFlagsResolved: boolean;
  isTwingateLoading: boolean;
  isInternalAccessNetworksLoading: boolean;
  isConnectorsLoading: boolean;
};

export type ApplicationInternalAccessSettingsProps = {
  draft: InternalApplicationDraft;
  inventory: InternalNetworkInventory;
  internalAccess: ApplicationInternalAccess | null;
  isInternalAccessLoading: boolean;
  isSavingBinding: boolean;
  onSaveBinding: (payload: InternalAccessBindingPayload) => Promise<unknown>;
  onTestConnection: () => Promise<InternalAccessTestResult>;
  isReadOnly?: boolean;
  onManageNetworks?: () => void;
};

export function ApplicationInternalAccessSettings({
  draft,
  inventory,
  internalAccess,
  isInternalAccessLoading,
  isSavingBinding,
  onSaveBinding,
  onTestConnection,
  isReadOnly,
  onManageNetworks,
}: ApplicationInternalAccessSettingsProps) {
  const t = useTranslations('designSystem.applicationSettings.networking');
  const tCommon = useTranslations('designSystem.common');
  const [hasUnsavedBinding, setHasUnsavedBinding] = useState(false);
  const [testResult, setTestResult] = useState<InternalAccessTestResult | null>(
    null,
  );
  const [isTesting, setIsTesting] = useState(false);
  const { flags } = inventory;
  const visibleInternalAccessNetworks = flags.isInternalAccessEnabled
    ? inventory.internalAccessNetworks
    : [];
  const networkCount =
    inventory.twingateNetworks.length + visibleInternalAccessNetworks.length;
  const hasNetworks = networkCount > 0;
  const hasMultipleNetworks = networkCount > 1;
  const hasConnectors = hasInternalConnectors({
    connectors: inventory.connectors,
    internalAccessNetworks: visibleInternalAccessNetworks,
  });
  const isInfrastructureLoading =
    !inventory.areFeatureFlagsResolved ||
    inventory.isTwingateLoading ||
    inventory.isConnectorsLoading ||
    inventory.isInternalAccessNetworksLoading;
  const showManageNetworkCallout = !isInfrastructureLoading && !hasNetworks;
  const canTurnInternalAppOn =
    hasNetworks && hasConnectors && !isInfrastructureLoading;
  const isToggleDisabled =
    isReadOnly ||
    isSavingBinding ||
    (!draft.isPersistedInternalApp &&
      !draft.isInternalApplication &&
      !canTurnInternalAppOn);
  const isEditorNetworksLoading =
    !inventory.areFeatureFlagsResolved ||
    (flags.isTwingateEnabled && inventory.isTwingateLoading) ||
    inventory.isInternalAccessNetworksLoading;
  const networkOptions = useMemo(
    () =>
      buildInternalNetworkOptions({
        internalAccessNetworks: inventory.internalAccessNetworks,
        twingateNetworks: inventory.twingateNetworks,
        flags,
        labels: {
          network: t('internalAccess.network'),
          internalAccess: t('internalAccess.transportInternalAccess'),
          resourceNetworks: t('internalAccess.transportResourceNetworks'),
          legacy: t('internalAccess.legacy'),
        },
      }),
    [flags, inventory.internalAccessNetworks, inventory.twingateNetworks, t],
  );

  useEffect(() => {
    if (hasUnsavedBinding) {
      setTestResult(null);
    }
  }, [hasUnsavedBinding]);

  const handleSaveDisable = async () => {
    try {
      await onSaveBinding({
        twingateNetworkId: null,
        internalAccessNetworkId: null,
      });
      toasterApi.success({ title: t('saveSuccess') });
    } catch (error) {
      console.error(
        new Error('Failed to disable internal application setting'),
        error,
      );
      toasterApi.error({ title: t('saveError') });
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);

    try {
      const result = await onTestConnection();

      setTestResult(result);

      if (result.status === 'success') {
        toasterApi.success({ title: t('internalAccess.testSuccess') });

        return;
      }

      toasterApi.error({ title: t('internalAccess.testFailed') });
    } catch (error) {
      console.error(new Error('Failed to run internal access test'), error);
      toasterApi.error({ title: t('internalAccess.testFailed') });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <VStack gap={4} alignItems="stretch" pt={4}>
      <VStack gap={2} alignItems="stretch">
        <SettingsBooleanControl
          checked={draft.isInternalApplication}
          label={draft.isInternalApplication ? tCommon('yes') : tCommon('no')}
          disabled={isToggleDisabled}
          onCheckedChange={({ checked }) =>
            draft.setIsInternalApplication(checked)
          }
        />
        <BodyDefault color="fg.muted">{t('isInternalAppHelp')}</BodyDefault>
        {showManageNetworkCallout && !draft.isInternalApplication ? (
          <ManageNetworkSetupCallout onManageNetworks={onManageNetworks} />
        ) : null}
        {draft.isPendingDisable && !isReadOnly ? (
          <HStack gap={2} alignItems="center">
            <BodyDefault color="fg.muted">
              {t('internalAccess.saveToDisable')}
            </BodyDefault>
            <Button
              size="sm"
              onClick={() => {
                void handleSaveDisable();
              }}
              loading={isSavingBinding}
            >
              {tCommon('saveChanges')}
            </Button>
          </HStack>
        ) : null}
      </VStack>
      {draft.isInternalApplication ? (
        <VStack gap={3} alignItems="stretch">
          {hasMultipleNetworks ? (
            <>
              <BodyDefault color="fg.muted">
                {t('internalAccess.description')}
              </BodyDefault>
              {onManageNetworks ? (
                <BodyDefault color="fg.muted">
                  {t('internalAccess.descriptionManagedPrefix')}
                  <ManageNetworkLinkButton
                    onManageNetworks={onManageNetworks}
                  />
                  .
                </BodyDefault>
              ) : null}
            </>
          ) : null}
          {!hasNetworks && !inventory.isTwingateLoading ? (
            <BodyDefault color="fg.muted">
              {t('internalAccess.noNetworksAvailable')}
            </BodyDefault>
          ) : null}
          {showManageNetworkCallout ? (
            <ManageNetworkSetupCallout onManageNetworks={onManageNetworks} />
          ) : null}
          {draft.isPersistedInternalApp && isInternalAccessLoading ? (
            <Skeleton h="20" w="full" />
          ) : hasNetworks ? (
            <InternalAccessBindingEditor
              internalAccess={internalAccess}
              networkOptions={networkOptions}
              flags={flags}
              areFeatureFlagsResolved={inventory.areFeatureFlagsResolved}
              isNetworksLoading={isEditorNetworksLoading}
              isPendingEnable={draft.isPendingEnable}
              isSaving={isSavingBinding}
              isReadOnly={isReadOnly}
              onDirtyChange={setHasUnsavedBinding}
              onSaveBinding={onSaveBinding}
            />
          ) : null}
          {!isReadOnly ? (
            <VStack gap={2} alignItems="stretch" w="full">
              {hasUnsavedBinding ? (
                <BodyDefault color="fg.muted">
                  {t('internalAccess.saveBeforeTest')}
                </BodyDefault>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                alignSelf="flex-start"
                loading={isTesting}
                disabled={
                  hasUnsavedBinding || draft.isPendingEnable || !internalAccess
                }
                onClick={() => {
                  void handleTestConnection();
                }}
              >
                {t('internalAccess.testConnection')}
              </Button>
            </VStack>
          ) : null}
          {testResult && !hasUnsavedBinding ? (
            <InternalAccessTestResults result={testResult} />
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
}
